/**
 * Confirmation dialog for a document-level review decision.
 *
 * Lifted out of ReviewStatusBanner so the response overview and the full
 * response reader can raise the same dialog (spec 29 §8). The notes textarea
 * lives here and resets whenever the dialog closes.
 */

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { useArtifactReviewsStore } from '@/stores/artifact-reviews-store'
import { useResponsesStore } from '@/stores/responses-store'
import { toast } from 'sonner'
import type { BidArtifact, ArtifactReview, ArtifactReviewDecision } from '@/lib/api'

export interface ReviewDecisionDialogProps {
  artifact: BidArtifact
  /** The pending review the decision applies to. */
  review: ArtifactReview
  /** null closes the dialog. */
  decision: ArtifactReviewDecision | null
  onClose: () => void
  onDecided?: (artifact: BidArtifact) => void
}

export function ReviewDecisionDialog({
  artifact,
  review,
  decision,
  onClose,
  onDecided,
}: ReviewDecisionDialogProps) {
  const decide = useArtifactReviewsStore((s) => s.decide)
  const deciding = useArtifactReviewsStore((s) => s.deciding[artifact.id] ?? false)
  const upsertArtifact = useResponsesStore((s) => s.upsertArtifact)

  const [overallNotes, setOverallNotes] = useState('')

  const isOpen = decision !== null

  // Notes are per-decision, never carried across an open/close cycle.
  useEffect(() => {
    if (!isOpen) setOverallNotes('')
  }, [isOpen])

  const handleDecide = async () => {
    if (!decision) return
    try {
      const res = await decide(artifact.id, review.id, decision, overallNotes.trim() || null)
      upsertArtifact(res.artifact)
      toast.success(decision === 'approve' ? 'Response approved' : 'Changes requested')
      onDecided?.(res.artifact)
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to record decision'
      toast.error(msg)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {decision === 'approve' ? 'Approve this response' : 'Request changes'}
          </DialogTitle>
          <DialogDescription>
            {decision === 'approve'
              ? 'Final sign-off. The response will be marked finalised.'
              : 'All approved and completed sections will go back to Changes requested so writers can address your feedback. They can re-submit when done.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm font-medium">Overall notes (optional)</label>
          <Textarea
            value={overallNotes}
            onChange={(e) => setOverallNotes(e.target.value)}
            placeholder={
              decision === 'approve'
                ? 'Looks good overall...'
                : 'Summarise what needs to change — inline comments cover the details.'
            }
            rows={4}
            maxLength={4000}
          />
          <p className="text-meta text-right">{overallNotes.length} / 4000</p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={deciding}>
            Cancel
          </Button>
          <Button onClick={() => void handleDecide()} disabled={deciding}>
            {deciding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {decision === 'approve' ? 'Approve' : 'Request changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
